/**
 * CommentItem
 *
 * Renders a single comment with:
 * - Author display name + role badge + relative timestamp
 * - Body text (or deleted placeholder)
 * - Action buttons: reply, edit (author only), delete (author/admin), report
 * - Edited indicator
 * - Optional video timestamp chip (calls onSeekToTimestamp when clicked)
 */

import { useState } from 'react';
import type { EvaluationComment, CommentMention } from '@/services/commentApi';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelativeTime(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

function roleBadgeClass(role: string | null): string {
  if (role === 'admin') return 'bg-danger/20 text-danger';
  if (role === 'coach') return 'bg-info/20 text-info';
  return 'bg-dark-elevated-2 text-text-muted';
}

// ─── Mention rendering ────────────────────────────────────────────────────────

/**
 * Split a comment body into text runs and resolved @mention tokens.
 *
 * Strategy:
 *  - Build a regex alternation from the backend-resolved display names,
 *    longest first so `@Alice Coach` matches before `@Alice`.
 *  - Each resolved name is escaped and prefixed with `@`.
 *  - Unresolved `@word` tokens pass through as plain text.
 *
 * Example:
 *   body    = "Nice work @Alice Coach, keep it up @bob!"
 *   mentions = [{display_name: "Alice Coach"}, {display_name: "bob"}]
 *   → ["Nice work ", "@Alice Coach", ", keep it up ", "@bob", "!"]
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function renderBodyWithMentions(
  body: string,
  mentions: CommentMention[] | undefined
): React.ReactNode {
  if (!mentions || mentions.length === 0) {
    return <span>{body}</span>;
  }

  // Build a set for O(1) lookup after splitting.
  const resolvedNames = new Map<string, CommentMention>();
  for (const m of mentions) {
    resolvedNames.set(m.display_name.toLowerCase(), m);
  }

  // Sort by length descending so the regex matches longest first.
  const sortedNames = [...mentions].sort(
    (a, b) => b.display_name.length - a.display_name.length
  );
  const pattern = sortedNames
    .map((m) => `@${escapeRegex(m.display_name)}`)
    .join('|');

  const parts = body.split(new RegExp(`(${pattern})`, 'gi'));

  return (
    <>
      {parts.map((part, idx) => {
        if (part.startsWith('@')) {
          const name = part.slice(1).toLowerCase();
          const resolved = resolvedNames.get(name);
          if (resolved) {
            return (
              <span
                key={idx}
                className="text-accent font-medium"
                title={`@${resolved.display_name}`}
              >
                {part}
              </span>
            );
          }
        }
        return <span key={idx}>{part}</span>;
      })}
    </>
  );
}

// ─── Visibility badge ─────────────────────────────────────────────────────────

function VisibilityBadge({ visibility }: { visibility: string }) {
  if (visibility === 'private') {
    return (
      <span
        className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-dark-elevated-2 text-text-muted"
        title="Visible to you and your coach only"
      >
        private
      </span>
    );
  }
  if (visibility === 'channel') {
    return (
      <span
        className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-accent/10 text-accent"
        title="Visible to all channel members"
      >
        channel
      </span>
    );
  }
  if (visibility === 'public') {
    return (
      <span
        className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-success/10 text-success"
        title="Visible to anyone with the link"
      >
        public
      </span>
    );
  }
  return null;
}

function formatTimestamp(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface CommentItemProps {
  comment: EvaluationComment;
  currentUserId: string | null;
  currentUserRole: string | null;
  /** Whether to indent (replies). */
  isReply?: boolean;
  onReply?: (comment: EvaluationComment) => void;
  onEdit?: (comment: EvaluationComment, newBody: string) => Promise<void>;
  onDelete?: (commentId: string) => Promise<void>;
  onReport?: (commentId: string) => void;
  onSeekToTimestamp?: (sec: number) => void;
}

export default function CommentItem({
  comment,
  currentUserId,
  currentUserRole,
  isReply = false,
  onReply,
  onEdit,
  onDelete,
  onReport,
  onSeekToTimestamp,
}: CommentItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editBody, setEditBody] = useState(comment.body);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');

  const isAuthor = currentUserId === comment.author_user_id;
  const isAdminOrCoach = currentUserRole === 'admin' || currentUserRole === 'coach';
  const canEdit = isAuthor && !comment.deleted;
  const canDelete = (isAuthor || isAdminOrCoach) && !comment.deleted;
  const canReport = !isAuthor;

  const handleSaveEdit = async () => {
    const trimmed = editBody.trim();
    if (!trimmed || !onEdit) return;
    setIsSaving(true);
    try {
      await onEdit(comment, trimmed);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || !window.confirm('Delete this comment?')) return;
    setIsDeleting(true);
    try {
      await onDelete(comment.id);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReport = async () => {
    if (!onReport || !reportReason.trim()) return;
    onReport(comment.id);
    setShowReportModal(false);
    setReportReason('');
  };

  return (
    <div
      className={`${isReply ? 'ml-6 border-l-2 border-border-subtle pl-4' : ''} py-2`}
    >
      {/* Header row */}
      <div className="flex flex-wrap items-center gap-2 mb-1">
        {/* Author name */}
        <span className="text-sm font-medium text-text-primary">
          {comment.author_display_name ?? 'Unknown'}
        </span>

        {/* Role badge */}
        {comment.author_role && (
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${roleBadgeClass(comment.author_role)}`}
          >
            {comment.author_role}
          </span>
        )}

        {/* Timestamp */}
        <span className="text-xs text-text-muted" title={comment.created_at}>
          {formatRelativeTime(comment.created_at)}
        </span>

        {/* Edited indicator */}
        {comment.edited_at && !comment.deleted && (
          <span className="text-[10px] text-text-muted italic">(edited)</span>
        )}

        {/* Video timestamp chip */}
        {comment.video_timestamp_sec != null && !comment.deleted && (
          <button
            type="button"
            onClick={() => onSeekToTimestamp?.(comment.video_timestamp_sec!)}
            className="rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium
              text-accent hover:bg-accent/20 transition-colors"
            title="Seek to this point in the video"
          >
            ▶ {formatTimestamp(comment.video_timestamp_sec)}
          </button>
        )}

        {/* Visibility badge */}
        {!comment.deleted && (
          <VisibilityBadge visibility={comment.visibility} />
        )}
      </div>

      {/* Body */}
      {comment.deleted ? (
        <p className="text-sm italic text-text-muted">
          This comment was deleted.
        </p>
      ) : isEditing ? (
        <div className="mt-1">
          <textarea
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            rows={3}
            maxLength={5000}
            className="w-full resize-none rounded border border-border-subtle
              bg-dark-base px-3 py-2 text-sm text-text-primary
              focus:outline-none focus:ring-1 focus:ring-accent"
            autoFocus
          />
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              onClick={() => void handleSaveEdit()}
              disabled={isSaving || !editBody.trim()}
              className="rounded bg-accent px-2 py-1 text-xs text-text-inverse
                disabled:opacity-40"
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setIsEditing(false); setEditBody(comment.body); }}
              className="rounded px-2 py-1 text-xs text-text-muted hover:text-text-primary"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-text-primary whitespace-pre-wrap">
          {renderBodyWithMentions(comment.body, comment.mentions)}
        </p>
      )}

      {/* Action row */}
      {!comment.deleted && !isEditing && (
        <div className="mt-1 flex items-center gap-3">
          {!isReply && onReply && (
            <button
              type="button"
              onClick={() => onReply(comment)}
              className="text-[11px] text-text-muted hover:text-text-primary transition-colors"
            >
              Reply
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="text-[11px] text-text-muted hover:text-text-primary transition-colors"
            >
              Edit
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={isDeleting}
              className="text-[11px] text-text-muted hover:text-danger transition-colors
                disabled:opacity-40"
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          {canReport && (
            <button
              type="button"
              onClick={() => setShowReportModal(true)}
              className="text-[11px] text-text-muted hover:text-warning transition-colors"
            >
              Report
            </button>
          )}
        </div>
      )}

      {/* Inline report modal */}
      {showReportModal && (
        <div className="mt-2 rounded border border-warning/30 bg-warning/5 p-3">
          <p className="mb-2 text-xs font-medium text-warning">
            Report this comment
          </p>
          <textarea
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            placeholder="Describe why you're reporting this comment…"
            rows={2}
            maxLength={1000}
            className="w-full resize-none rounded border border-border-subtle
              bg-dark-base px-2 py-1 text-xs text-text-primary
              focus:outline-none focus:ring-1 focus:ring-warning"
          />
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => void handleReport()}
              disabled={!reportReason.trim()}
              className="rounded bg-warning px-2 py-1 text-xs text-dark-base
                disabled:opacity-40"
            >
              Submit Report
            </button>
            <button
              type="button"
              onClick={() => { setShowReportModal(false); setReportReason(''); }}
              className="rounded px-2 py-1 text-xs text-text-muted hover:text-text-primary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
